<template>
  <b-card class="page-view-table">
    <div
      v-if="onProgress"
      class="data-loading d-flex justify-content-center"
    >
      <b-spinner
        class="mt-1"
        label="Loading"
      />
    </div>
    <!-- search input -->
    <div class="custom-search d-flex justify-content-end">
      <b-form-group>
        <div class="d-flex align-items-center">
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            @input="handleSearch"
          />
        </div>
      </b-form-group>
    </div>
    <!-- tables -->
    <vue-good-table
      :columns="columns"
      :rows="rows"
      :rtl="direction"
      style-class="vgt-table"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
        searchFn: searchTable,
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
      @on-sort-change="onSortChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Column: index -->
        <span
          v-if="props.column.field === 'index'"
          class="text-nowrap"
        >
          <span class="text-nowrap">{{ props.row.originalIndex + 1 }}</span>
        </span>

        <!-- Column: zones -->
        <span
          v-else-if="props.column.field === 'zones'"
          class="text-nowrap"
        >
          <b-badge
            v-for="(zone, index) in props.row.zones"
            :key="`zone_${index}`"
            v-b-tooltip.hover
            variant="light"
            class="d-block mb-1 zone-item"
            size="sm"
          >
            <span>{{ zone.zone_name.replace('Slider', '') }} - {{ zone.zone_value_count.toLocaleString() }}</span>
          </b-badge>
        </span>

        <span
          v-else-if="props.column.field === 'pvCount'"
          class="page-view-item"
        >
          <span
            v-if="props.row.pvCount"
            class="page-view-count font-weight-bold"
          >{{ props.row.pvCount.toLocaleString() }}</span><br>
          <span
            v-if="props.row.avgPageDuration"
            class="avg-page-duration"
          >
            <b-badge
              v-b-tooltip.hover
              variant="light"
              title="Average Page Duration"
            >
              <feather-icon
                icon="ClockIcon"
                class="mr-25"
              />
              <span>{{ props.row.avgPageDuration }}</span>
            </b-badge>
          </span>
        </span>

        <!-- Column: articles -->
        <span v-else-if="props.column.field === 'breadcrumb'">
          <span class="article-title">
            <span
              class="font-weight-bold breadcrumb-name"
              v-html="props.row.breadcrumb"
            />
          </span>
          <span class="article-details">
            <span
              v-if="props.row.referers"
            >
              <b-badge
                v-for="(referer, index) in getReferersSlice(props.row.referers)"
                :key="`referer-item-${index}`"
                v-b-tooltip.hover
                :class="`referer-item ${referer.className}`"
                variant="light"
                :title="`Count: ${referer.referer_count.toLocaleString()} / Ratio: ${referer.ratio.toFixed(4)}%`"
              >
                <span v-if="referer.referer">{{ referer.referer }} {{ referer.ratio.toFixed(2) }}%</span>
                <span v-else>Direct</span>
              </b-badge>
              <div v-if="props.row.refererUtms!==undefined && props.row.refererUtms.length > 0">
                <span class="font-weight-bolder">UTM </span>
                <b-badge
                  v-for="(referer, index) in getRefererUtmsSlice(props.row.refererUtms)"
                  :key="`referer-utm-item-${index}`"
                  v-b-tooltip.hover
                  :class="`referer-utm-item ${referer.className}`"
                  variant="light"
                  :title="`Count: ${referer.referer_count.toLocaleString()} / Ratio: ${referer.ratio.toFixed(4)}%`"
                >
                  <span
                    v-if="referer.referer"
                    :class="getReferrerCapitalizeClass(referer.referer)"
                  >{{ referer.referer }} {{ referer.ratio.toFixed(2) }}%</span>
                </b-badge>
              </div>
            </span>
            <div
              v-if="props.row.originReferers"
              class="source-referrers"
            >
              <span>Source Referrers</span>
              <b-badge
                v-for="(referer, index) in getRefererUtmsSlice(props.row.originReferers)"
                :key="`referer-item-${index}`"
                v-b-tooltip.hover
                :class="`referer-item ${referer.className}`"
                variant="light"
                :title="`Count: ${referer.referer_count.toLocaleString()} / Ratio: ${referer.ratio.toFixed(5)}%`"
              >
                <span
                  v-if="referer.referer"
                  :class="getReferrerCapitalizeClass(referer.referer)"
                >{{ referer.referer }} {{ referer.ratio.toFixed(2) }}%</span>
                <span v-else>Direct</span>
              </b-badge>
              <div v-if="props.row.originUtmsReferers.length">
                <span class="font-weight-bolder">UTM </span>
                <b-badge
                  v-for="(referer, index) in getOriginUtmsReferersSlice(props.row.originUtmsReferers)"
                  :key="`origin-utm-referer-item-${index}`"
                  v-b-tooltip.hover
                  :class="`origin-utm-referer-item ${referer.className}`"
                  variant="light"
                  :title="`Count: ${referer.referer_count.toLocaleString()} / Ratio: ${referer.ratio.toFixed(5)}%`"
                >
                  <span
                    v-if="referer.referer"
                    :class="getReferrerCapitalizeClass(referer.referer)"
                  >{{ referer.referer }} {{ referer.ratio.toFixed(2) }}%</span>
                </b-badge>
              </div>
            </div>
          </span>
        </span>
        <!-- Column: articles -->

        <span v-else-if="props.column.field === 'readability'">
          <b-badge
            v-if="props.row.readability > 0"
            :variant="readabilityClassName(props.row.readability)"
            class="badge-glow badge-readability"
          >
            {{ props.row.readability }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'galleryReadability'">
          <b-badge
            v-if="props.row.galleryReadability > 0"
            :variant="readabilityClassName(props.row.galleryReadability)"
            class="badge-glow badge-readability"
          >
            {{ props.row.galleryReadability }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'recirculation'">
          <span
            v-if="props.row.recirculation > 0"
          >
            <b-badge
              v-b-tooltip.hover
              variant="light"
              title="Average Recirculation"
            >
              <span>{{ props.row.recirculation }}%</span>
            </b-badge>
          </span>
        </span>

        <span v-else-if="props.column.field === 'avgScrollPercentage'">
          <span>
            <span
              v-if="props.row.avgScrollPercentage"
              class="avg-page-duration"
            >
              <b-badge
                v-b-tooltip.hover
                variant="light"
                title="Average Scroll Percentage"
              >
                <span>{{ props.row.avgScrollPercentage }}%</span>
              </b-badge>
            </span>
          </span>
        </span>
        <span v-else-if="props.column.field === 'galleryViewAvg'">
          <span
            v-if="props.row.galleryViewAvg && props.row.galleryQuantityAvg"
          >
            <b-badge
              v-b-tooltip.hover
              variant="light"
              title="Average Gallery View"
            >
              <span>{{ props.row.galleryViewAvg }} / {{ props.row.galleryQuantityAvg }}</span>
            </b-badge>
          </span>
        </span>
        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">

          <!-- page length -->
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap ">
              Showing 1 to
            </span>
            <b-form-select
              v-model="pageLength"
              :options="pages"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> of {{ props.total }} entries </span>
          </div>

          <!-- pagination -->
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
    BCard, BPagination, BFormGroup, BFormInput, BFormSelect, BBadge, VBTooltip, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import Prism from 'vue-prism-component'
import store from '@/store'
import { getCoreApiByObjectId } from '@/coreApi/coreApiHelper'

// eslint-disable-next-line import/no-extraneous-dependencies
const moment = require('moment')

export default {
    components: {
        BCard,
        VueGoodTable,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        Prism,
        BBadge,
        BFormCheckbox,
        BSpinner,
    },
    directives: {
        'b-tooltip': VBTooltip,
    },
    props: {
        tableObject: {
            type: Object,
            required: true,
        },
        onProgress: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            pageLength: 100,
            dir: false,
            pages: ['100', '200'],
            columns: [
                {
                    label: '',
                    field: 'index',
                    sortable: false,
                    thClass: 'table-index-item-th',
                    tdClass: 'text-center align-middle table-index-item',
                },
                {
                    label: 'Zones',
                    field: 'zones',
                    sortable: false,
                    thClass: 'zone-column-desktop-th',
                    tdClass: 'zone-column-desktop',
                },
                {
                    label: 'Views',
                    field: 'pvCount',
                    type: 'number',
                    sortable: true,
                    tdClass: 'text-left page-view-column',
                },
                {
                    label: 'Breadcrumbs',
                    field: 'breadcrumb',
                    sortable: true,
                    thClass: 'page-title-column-th',
                    tdClass: 'text-left align-top page-title-column',
                },
                {
                    label: 'Zones',
                    field: 'zones',
                    sortable: false,
                    thClass: 'zone-column-mobile-th',
                    tdClass: 'zone-column-mobile',
                },
                {
                    label: 'Readability',
                    field: 'readability',
                    type: 'number',
                    sortable: true,
                    tdClass: 'text-center align-middle readability-column',
                },
                {
                    label: 'Scroll',
                    field: 'avgScrollPercentage',
                    type: 'number',
                    sortable: true,
                    tdClass: 'text-center align-middle',
                },
                {
                    label: 'Gallery Readability',
                    field: 'galleryReadability',
                    type: 'number',
                    sortable: true,
                    tdClass: 'text-center align-middle readability-column',
                },
                {
                    label: 'Gallery Scroll',
                    field: 'galleryViewAvg',
                    type: 'number',
                    sortable: true,
                    tdClass: 'text-center align-middle',
                },
                {
                    label: 'Recirculation',
                    field: 'recirculation',
                    type: 'number',
                    sortable: true,
                    tdClass: 'text-center align-middle',
                },

            ],
            rows: [],
            allRows: [],
            searchTerm: '',
            status: [{
                1: 'Current', 2: 'Professional', 3: 'Rejected', 4: 'Resigned', 5: 'Applied',
            },
            {
                1: 'light-primary', 2: 'light-success', 3: 'light-danger', 4: 'light-warning', 5: 'light-info',
            }],
            filterShowTodayPages: false,
            filterShowAllPages: false,
        }
    },
    computed: {
        direction() {
            if (store.state.appConfig.isRTL) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dir = true
                return this.dir
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dir = false
            return this.dir
        },
    },
    watch: {
        tableObject() {
            this.allRows = this.tableData()
            this.changeRows()
        },
    },
    created() {
        this.allRows = this.tableData()
        this.changeRows()
    },
    methods: {
        tableData() {
            const object = getCoreApiByObjectId(this.tableObject, 'tableData')
            // console.log('object', object)
            /* {
  "breadcrumb": "fzozdemir",
  "pvCount": 3405374,
  "avgScrollPercentage": 69.14,
  "avgPageDuration": 16.53,
  "readability": 12.7,
  "galleryQuantityAvg": 4,
  "galleryViewAvg": 2,
  "avgGalleryScrollPercentage": 80.9,
  "avgGalleryPageDuration": 33.5,
  "galleryReadability": "19.0",
  "generatedTrafficTotal": 547023,
  "generatedTrafficNoIScrollTotal": 15590,
  "recirculation": 0.46,
  "referers": "[{\"referer\":\"www-hurriyet-com-tr.cdn.ampproject.org\",\"referer_count\":35733},{\"referer\":\"site_internal_infinite_scroll\",\"referer_count\":514190},{\"referer\":\"site_internal\",\"referer_count\":22141},{\"referer\":\"site_direct\",\"referer_count\":67212},{\"referer\":\"search_google_organic\",\"referer_count\":2729986}]"
}
 */
            const tableData = []
            if (
                object
          && 'data' in object
            ) {
                object.data.forEach(objectItem => {
                    const item = { ...objectItem }

                    if (item.avgPageDuration) {
                        item.avgPageDuration = moment.utc(item.avgPageDuration * 1000).format('mm:ss')
                    }

                    if (item.referers) {
                        item.referers = JSON.parse(item.referers)
                        item.referers = item.referers.sort((a, b) => b.referer_count - a.referer_count)
                        item.referers.forEach((referer, index) => {
                            item.referers[index].ratio = (Number(referer.referer_count / item.pvCount) * 100)
                            const refererLabel = referer.referer
                                .replace('site_internal_', '')
                                .replace('search_', '')
                                .replace('social_', '')
                                .replace('site_', '')

                            if (referer.referer.includes('site_internal')) {
                                item.referers[index].className = 'referer-site-internal'
                            } else if (referer.referer.includes('search_')) {
                                item.referers[index].className = 'referer-search'
                            } else if (referer.referer.includes('social_')) {
                                item.referers[index].className = 'referer-social'
                            } else {
                                item.referers[index].className = 'referer-default'
                            }

                            item.referers[index].referer = refererLabel.replace('_', ' ')
                        })
                    } else if (!item.referers) {
                        item.referers = []
                    }

                    if (item.originReferers) {
                        item.originReferers = JSON.parse(item.originReferers)
                        item.originReferers = item.originReferers.sort((a, b) => b.referer_count - a.referer_count)
                        item.originReferers.forEach((referer, index) => {
                            item.originReferers[index].ratio = (Number(referer.referer_count / item.pvCount) * 100)
                            let refererLabel = referer.referer
                                .replace('site_internal_', '')
                                .replace('search_', '')
                                .replace('social_', '')
                                .replace('site_', '')
                                .replace('referrer', '')

                            if (referer.referer.includes('site_internal')) {
                                item.originReferers[index].className = 'referer-site-internal'
                            } else if (referer.referer.includes('search_')) {
                                item.originReferers[index].className = 'referer-search'
                            } else if (referer.referer.includes('social_')) {
                                item.originReferers[index].className = 'referer-social'
                            } else {
                                item.originReferers[index].className = 'referer-default'
                            }

                            if (referer.referer.includes('ampproject')) {
                                refererLabel = 'AMP'
                            }

                            item.originReferers[index].referer = refererLabel.replace('_', ' ')
                        })
                    } else if (!item.originReferers) {
                        item.originReferers = []
                    }

                    // Referer Utms Start
                    if (item.refererUtms) {
                        item.refererUtms = JSON.parse(item.refererUtms)
                        item.refererUtms = item.refererUtms.sort((a, b) => b.referer_count - a.referer_count)
                        item.refererUtms.forEach((referer, index) => {
                            item.refererUtms[index].ratio = (Number(referer.referer_count / item.pvCount) * 100)
                            item.refererUtms[index].className = 'referer-utm-default'
                            item.refererUtms[index].referer = referer.referer ? referer.referer.replaceAll('#', ' ').trim() : ''
                        })
                    } else if (!item.refererUtms) {
                        item.refererUtms = []
                    }
                    // Referer Utms End

                    //  Origin Referer Utms Start
                    if (item.originUtmsReferers) {
                        item.originUtmsReferers = JSON.parse(item.originUtmsReferers)
                        item.originUtmsReferers = item.originUtmsReferers.sort((a, b) => b.referer_count - a.referer_count)
                        item.originUtmsReferers.forEach((referer, index) => {
                            item.originUtmsReferers[index].ratio = (Number(referer.referer_count / item.pvCount) * 100)
                            item.originUtmsReferers[index].className = 'referer-utm-default'
                            item.originUtmsReferers[index].referer = referer.referer ? referer.referer.replaceAll('#', ' ').trim() : ''
                        })
                    } else if (!item.originUtmsReferers) {
                        item.originUtmsReferers = []
                    }
                    // Origin Referer Utms End

                    if (typeof item.readability !== 'undefined' && item.readability) {
                        // eslint-disable-next-line radix
                        item.readability = parseInt(item.readability)
                    }

                    if (item.readability) {
                        if (item.readability > 100) {
                            item.readability = 100
                        }
                    } else {
                        item.readability = 0
                    }
                    item.readability = item.readability.toFixed(0)

                    if (typeof item.galleryReadability !== 'undefined' && item.galleryReadability) {
                        // eslint-disable-next-line radix
                        item.galleryReadability = parseInt(item.galleryReadability)
                    }

                    if (item.galleryReadability) {
                        if (item.galleryReadability > 100) {
                            item.galleryReadability = 100
                        }
                    } else {
                        item.galleryReadability = 0
                    }
                    item.galleryReadability = item.galleryReadability.toFixed(0)

                    if (!item.recirculation) {
                        item.recirculation = 0
                    }

                    item.recirculation = item.recirculation.toFixed(1)

                    if (item.zones) {
                        item.zones = JSON.parse(item.zones)
                        item.zones = item.zones.sort((a, b) => a.zone_value_count > b.zone_value_count)
                    } else if (!item.zones) {
                        item.zones = []
                    }
                    tableData.push(item)
                })
            }

            return tableData
        },
        filterShowTodayPagesChanged() {
            this.filterShowAllPages = false
            this.changeRows()
        },
        filterShowAllPagesChanged() {
            this.filterShowTodayPages = false
            this.changeRows()
        },
        changeRows() {
            // let rows = this.allRows
            // if (!this.filterShowAllPages) {
            //     rows = this.allRows.filter(obj => ['Content', 'Gallery'].indexOf(obj.breadcrumb) > -1)
            // }
            //
            // if (this.filterShowTodayPages) {
            //     rows = rows.filter(obj => obj.datePublished && obj.datePublished.indexOf('Today') > -1)
            // }
            //
            // this.rows = rows
            this.rows = this.allRows
        },
        handleSearch(searching) {
        },
        handleChangePage(page) {
        },
        handlePageChange(active) {
        },
        onSortChange(params) {
        },
        readabilityClassName(value) {
            let className = 'success'
            if (value <= 20) {
                className = 'danger'
            } else if (value <= 40) {
                className = 'warning'
            } else if (value <= 60) {
                className = 'info'
            } else if (value <= 80) {
                className = 'primary'
            } else {
                className = 'success'
            }

            return className
        },
        searchTable(row, col, cellValue, searchTerm) {
            return !searchTerm ? true : (row.breadcrumb && row.breadcrumb.includes(searchTerm))
        },
        isMobile() {
            return window.innerWidth < 768
        },
        getReferersSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getOriginReferrersSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getRefererUtmsSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getOriginUtmsReferersSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getReferrerCapitalizeClass(referer) {
            return !(referer.includes('www') || referer.includes('com')) ? 'text-transform-capitalize' : ''
        },
    },
}
</script>

<style lang="scss" >
@import '~@core/scss/vue/libs/vue-good-table.scss';
</style>
